import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Lock, User, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BANNER_URL = "https://customer-assets.emergentagent.com/job_b5cb1511-d064-4852-992c-1923642a6a2e/artifacts/a9e22iqs_k.jbkln..mp4";

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || (err.code === 'ERR_NETWORK' ? 'Servidor indisponivel.' : 'Erro ao realizar login');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="login-page">
      <div className="w-full max-w-md mx-auto">
        {/* Banner */}
        <div className="w-full rounded-t-2xl overflow-hidden bg-[#0a1628]" data-testid="login-banner">
          <video
            src={BANNER_URL}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-auto"
            data-testid="login-video"
          />
        </div>

        {/* Login Card */}
        <div className="px-10 py-8 bg-white">
          <div className="text-center mb-8">
            <h1 className="text-xl font-extrabold text-gray-900 uppercase tracking-wider" data-testid="login-title">
              ACESSO AO SISTEMA
            </h1>
            <p className="text-gray-400 text-[11px] font-medium tracking-[0.25em] mt-2 uppercase">
              MONITORAMENTO DE OPERACOES RPAS
            </p>
          </div>

          {error && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="login-error">
              <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
              <span className="text-red-600 text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-gray-900 text-xs font-bold uppercase tracking-wider">Usuario</Label>
              <div className="relative mt-1.5">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 h-12 rounded-lg border-gray-300 text-sm"
                  placeholder="Digite seu usuario"
                  required
                  data-testid="username-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-900 text-xs font-bold uppercase tracking-wider">Senha</Label>
              <div className="relative mt-1.5">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 rounded-lg border-gray-300 text-sm"
                  placeholder="Digite sua senha"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-lg font-bold text-sm uppercase tracking-wider"
              style={{ backgroundColor: '#4DD9E8', color: 'white' }}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>AUTENTICANDO...</span>
                </div>
              ) : (
                'ACESSAR SISTEMA'
              )}
            </Button>
          </form>

          <div className="mt-8 pt-5 border-t border-gray-200 text-center">
            <p className="text-gray-900 text-xs font-bold">monitoramentorpas.com</p>
            <p className="text-gray-400 text-[10px] mt-1">Todos os direitos reservados</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
